import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { api, fehlertext } from "./api";
import { Auswahl, Eingabe, Fehler, Feld, Karte, Knopf } from "./ui";

/**
 * Zugangsinformationen am Objekt: Schluesselnummer, Alarmcode, Torcode, WLAN.
 *
 * Dieselbe Ansicht im Buero und draussen. Im Buero haengt sie in der
 * Objektakte, draussen am Termin auf Mein Tag. Der Unterschied ist nur, dass
 * das Buero pflegen darf; das entscheidet die API und nicht diese Datei.
 *
 * Zwei Dinge, die man spaeter nachlesen will:
 *
 * Der Wert kommt erst auf Tippen und nicht mit der Liste. Das ist keine
 * Schoenheit, sondern der Grund, warum im Protokoll steht, wer welchen Code
 * wann gelesen hat. Kaeme er mit der Liste, stuende dort nur, dass jemand eine
 * Seite geoeffnet hat.
 *
 * Ein sichtbarer Code verschwindet nach anderthalb Minuten von selbst wieder.
 * Ein Telefon, das auf der Poolumrandung liegt, zeigt sonst den Alarmcode, bis
 * der Bildschirm ausgeht, und wer daneben steht, liest mit.
 */

const SICHTBAR_MS = 90_000;

interface Eintrag {
  id: string;
  art: string;
  bezeichnung: string;
  geaendert: string;
  geaendertVon: string | null;
}

const ARTEN = ["key", "alarm", "gate", "wifi", "safe", "other"] as const;

/**
 * @param flach fuer die Anzeige innerhalb einer bestehenden Karte, etwa am
 *   Termin auf Mein Tag. Ohne sie stuende dort ein Kasten im Kasten.
 */
export function ZugangAmObjekt({ objektId, flach = false }: { objektId: string; flach?: boolean }) {
  const { t } = useTranslation();
  const [eintraege, setEintraege] = useState<Eintrag[] | null>(null);
  const [darfPflegen, setDarfPflegen] = useState(false);
  const [verboten, setVerboten] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [neu, setNeu] = useState(false);
  const [bearbeitet, setBearbeitet] = useState<Eintrag | null>(null);

  const laden = useCallback(async () => {
    try {
      const a = await api.get<{ eintraege: Eintrag[]; darfPflegen: boolean }>(
        `/objekte/${objektId}/zugang`
      );
      setEintraege(a.eintraege);
      setDarfPflegen(a.darfPflegen);
      setVerboten(false);
    } catch {
      /*
       * 404 heisst hier nicht "kaputt", sondern "geht dich nichts an". Die API
       * antwortet bewusst so, damit sich aus dem Unterschied zu 403 nicht
       * ablesen laesst, wo es ueberhaupt einen Alarm gibt. Fuer die Anzeige
       * heisst das: nichts zeigen, nicht meckern.
       */
      setVerboten(true);
      setEintraege([]);
    }
  }, [objektId]);

  useEffect(() => { void laden(); }, [laden]);

  if (verboten || eintraege === null) return null;
  if (eintraege.length === 0 && !darfPflegen) return null;

  const Rahmen = flach ? FlacherRahmen : Karte;

  return (
    <Rahmen className={flach ? "" : "lg:col-span-2"}>
      <div className="mb-3 flex flex-wrap items-baseline gap-3">
        <h2 className="font-bold">{t("zugang.titel")}</h2>
        <span className="text-sm text-leise">{t("zugang.hinweis")}</span>
        {darfPflegen && (
          <div className="ms-auto">
            <Knopf art="neben" onClick={() => setNeu(true)}>{t("zugang.neu")}</Knopf>
          </div>
        )}
      </div>
      <Fehler text={fehler} />

      {eintraege.length === 0 ? (
        <p className="text-sm text-leise">{t("zugang.leer")}</p>
      ) : (
        <ul className="space-y-2">
          {eintraege.map((e) => (
            <Zeile
              key={e.id}
              eintrag={e}
              objektId={objektId}
              darfPflegen={darfPflegen}
              onFehler={setFehler}
              onBearbeiten={() => setBearbeitet(e)}
            />
          ))}
        </ul>
      )}

      {(neu || bearbeitet) && (
        <EintragDialog
          objektId={objektId}
          eintrag={bearbeitet}
          onClose={() => { setNeu(false); setBearbeitet(null); }}
          onFertig={() => { setNeu(false); setBearbeitet(null); void laden(); }}
        />
      )}
    </Rahmen>
  );
}

function FlacherRahmen({ children }: { children: ReactNode; className?: string }) {
  return <div className="border-t border-linie pt-4">{children}</div>;
}

function Zeile({
  eintrag, objektId, darfPflegen, onFehler, onBearbeiten
}: {
  eintrag: Eintrag;
  objektId: string;
  darfPflegen: boolean;
  onFehler: (t: string | null) => void;
  onBearbeiten: () => void;
}) {
  const { t } = useTranslation();
  const [wert, setWert] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const uhr = useRef<number | null>(null);

  // Der Zeitgeber muss auch dann weg, wenn die Karte vorher verschwindet.
  useEffect(() => () => { if (uhr.current) window.clearTimeout(uhr.current); }, []);

  async function anzeigen() {
    setLaeuft(true);
    onFehler(null);
    try {
      const a = await api.get<{ wert: string }>(`/objekte/${objektId}/zugang/${eintrag.id}`);
      setWert(a.wert);
      if (uhr.current) window.clearTimeout(uhr.current);
      uhr.current = window.setTimeout(() => setWert(null), SICHTBAR_MS);
    } catch (e) {
      onFehler(fehlertext(e, t("fehler.laden")));
    } finally {
      setLaeuft(false);
    }
  }

  function verbergen() {
    if (uhr.current) window.clearTimeout(uhr.current);
    setWert(null);
  }

  return (
    <li className="rounded border border-linie p-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-2">
        <span className="rounded bg-grund px-2 py-0.5 text-xs font-semibold">
          {t(`zugang.art.${eintrag.art}`)}
        </span>
        <span className="font-semibold">{eintrag.bezeichnung}</span>

        <div className="ms-auto flex flex-wrap gap-2">
          {wert === null ? (
            <Knopf art="neben" disabled={laeuft} onClick={() => void anzeigen()}>
              {t("zugang.anzeigen")}
            </Knopf>
          ) : (
            <Knopf art="neben" onClick={verbergen}>{t("zugang.verbergen")}</Knopf>
          )}
          {darfPflegen && (
            <Knopf art="neben" onClick={onBearbeiten}>{t("aktion.bearbeiten")}</Knopf>
          )}
        </div>
      </div>

      {wert !== null && (
        <>
          {/* Gross und in gleicher Zeichenbreite: das hier wird an einer
              Tastatur am Tor abgetippt, oft bei Sonne und in Eile. */}
          <p className="mt-2 select-all break-all font-mono text-xl font-bold">{wert}</p>
          <p className="mt-1 text-xs text-leise">{t("zugang.verstecktGleich")}</p>
        </>
      )}
    </li>
  );
}

function EintragDialog({
  objektId, eintrag, onClose, onFertig
}: {
  objektId: string;
  eintrag: Eintrag | null;
  onClose: () => void;
  onFertig: () => void;
}) {
  const { t } = useTranslation();
  const [art, setArt] = useState(eintrag?.art ?? "gate");
  const [bezeichnung, setBezeichnung] = useState(eintrag?.bezeichnung ?? "");
  const [wert, setWert] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  async function speichern() {
    setLaeuft(true);
    setFehler(null);
    try {
      if (eintrag) {
        /*
         * Beim Aendern bleibt das Wertfeld leer, bis jemand hineinschreibt.
         * Den bestehenden Code vorzublenden hiesse, ihn bei jedem Umbenennen
         * erneut auf den Bildschirm zu holen, ohne dass es noetig waere.
         */
        await api.patch(`/objekte/${objektId}/zugang/${eintrag.id}`, {
          art, bezeichnung, ...(wert ? { wert } : {})
        });
      } else {
        await api.post(`/objekte/${objektId}/zugang`, { art, bezeichnung, wert });
      }
      onFertig();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
      setLaeuft(false);
    }
  }

  async function loeschen() {
    if (!eintrag) return;
    setLaeuft(true);
    try {
      await api.del(`/objekte/${objektId}/zugang/${eintrag.id}`);
      onFertig();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
      setLaeuft(false);
    }
  }

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-card bg-white p-6">
        <h2 className="mb-4 text-lg font-bold">
          {eintrag ? t("aktion.bearbeiten") : t("zugang.neu")}
        </h2>
        <Fehler text={fehler} />

        <div className="grid gap-3 sm:grid-cols-2">
          <Feld label={t("zugang.artLabel")}>
            <Auswahl value={art} onChange={(e) => setArt(e.target.value)}>
              {ARTEN.map((a) => <option key={a} value={a}>{t(`zugang.art.${a}`)}</option>)}
            </Auswahl>
          </Feld>
          <Feld label={t("zugang.bezeichnung")} hinweis={t("zugang.bezeichnungHinweis")}>
            <Eingabe value={bezeichnung} onChange={(e) => setBezeichnung(e.target.value)} />
          </Feld>
          <Feld
            label={t("zugang.wert")}
            hinweis={eintrag ? t("zugang.wertUnveraendert") : undefined}
            breit
          >
            <Eingabe value={wert} onChange={(e) => setWert(e.target.value)} />
          </Feld>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <Knopf
            disabled={laeuft || !bezeichnung.trim() || (!eintrag && !wert)}
            onClick={() => void speichern()}
          >
            {t("aktion.speichern")}
          </Knopf>
          <Knopf art="neben" onClick={onClose}>{t("aktion.abbrechen")}</Knopf>
          {eintrag && (
            <Knopf art="warnung" disabled={laeuft} onClick={() => void loeschen()}>
              {t("aktion.loeschen")}
            </Knopf>
          )}
        </div>
      </div>
    </div>
  );
}
